import React from 'react'
import { ITimetrackInterface, TimeTrackCreate } from 'Types/TimeTrack/ITimeTrack'
import styles from './track.module.scss'
import editIcon from 'assets/img/edit-icon.svg'
import deleteIcon from 'assets/img/delete-icon.svg'
import moment from 'moment'

interface IProps {
  timetracks: ITimetrackInterface[]
  loading: boolean
  setDeleteModal: (open: boolean) => void
  setDeleteData: (id: ITimetrackInterface) => void
  setTimeTrack: (track: TimeTrackCreate) => void
  setCreateModal: (open: boolean) => void
}

const PROJECT_COLORS = ['#73b2ca', '#799dec', '#a373e6', '#70e0d3', '#f2c14e', '#ec9a6f', '#a6df6d']

const projectColor = (projectId: number) => PROJECT_COLORS[projectId % PROJECT_COLORS.length]

// The API sends roles as enum names, e.g. FRONTEND_DEVELOPER -> Frontend developer
const formatRole = (role: unknown) =>
  typeof role === 'string'
    ? role.charAt(0) + role.slice(1).toLowerCase().replace(/_/g, ' ')
    : undefined

const Track: React.FC<IProps> = (props: IProps) => {
  function convertTimeTrack(timeTrack: ITimetrackInterface) {
    const track = new TimeTrackCreate()
    track.id = timeTrack.id
    track.chargeableHours = timeTrack.chargeableHours
    track.startDate = timeTrack.startDate
    track.description = timeTrack.description
    track.projectId = timeTrack.project.id
    track.projectRole = timeTrack.projectRole
    track.workingHours = timeTrack.workingHours

    props.setTimeTrack(track)
  }

  const day = moment(props.timetracks[0].startDate)
  const totalHours = props.timetracks.reduce((sum, track) => sum + track.workingHours, 0)
  const totalChargeable = props.timetracks.reduce((sum, track) => sum + track.chargeableHours, 0)

  return (
    <section className={styles.day}>
      <header className={styles.dayHeader}>
        <div className={styles.dayTitle}>
          <span className={styles.weekday}>{day.format('dddd')}</span>
          <span className={styles.date}>{day.format('DD.MM.YYYY.')}</span>
          {day.isSame(moment(), 'day') && <span className={styles.today}>Today</span>}
        </div>
        <div className={styles.dayTotals}>
          <span className={styles.totalHours}>{totalHours}h</span>
          {totalChargeable !== totalHours && (
            <span className={styles.chargeable}>{totalChargeable}h chargeable</span>
          )}
        </div>
      </header>

      <ul className={styles.entries}>
        {props.timetracks.map((track) => (
          <li className={styles.entry} key={track.id}>
            <div className={styles.entryTop}>
              <span
                className={styles.project}
                style={{ borderColor: projectColor(track.project.id) }}
              >
                <span
                  className={styles.projectDot}
                  style={{ backgroundColor: projectColor(track.project.id) }}
                />
                <span className={styles.projectName}>{track.project.projectName}</span>
              </span>
              {formatRole(track.projectRole) && (
                <span className={styles.role}>{formatRole(track.projectRole)}</span>
              )}

              <div className={styles.hours}>
                <span className={styles.hoursValue}>{track.workingHours}h</span>
                {track.chargeableHours !== track.workingHours && (
                  <span className={styles.hoursChargeable}>
                    {track.chargeableHours}h chargeable
                  </span>
                )}
              </div>

              <div className={styles.actions}>
                <button
                  type="button"
                  className={styles.actionButton}
                  aria-label="Edit entry"
                  title="Edit"
                  onClick={() => {
                    props.setCreateModal(true)
                    convertTimeTrack(track)
                  }}
                >
                  <img src={editIcon} alt="" />
                </button>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.deleteButton}`}
                  aria-label="Delete entry"
                  title="Delete"
                  onClick={() => {
                    props.setDeleteModal(true)
                    props.setDeleteData(track)
                  }}
                >
                  <img src={deleteIcon} alt="" />
                </button>
              </div>
            </div>

            <p className={styles.description}>{track.description}</p>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default Track
